import { useState } from 'react';
import { Box, Stack, Typography, IconButton, Popover } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { PopulationIcon } from '../../../../icons';

interface FilterComponentProps {
  onSortChange: (value: string) => void;
  title?: string;
}

export const FilterComponent: React.FC<FilterComponentProps> = ({ 
  onSortChange,
  title = "Population" 
}) => {
  const theme = useTheme();
  const [sortOrder, setSortOrder] = useState('highest');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (value: string) => {
    setSortOrder(value);
    onSortChange(value);
    handleClose();
  };

  const Option = ({ value, label }: { value: string; label: string }) => (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      onClick={() => handleOptionClick(value)}
      sx={{
        padding: '0.75rem 1rem',
        cursor: 'pointer',
        backgroundColor: sortOrder === value ? theme.palette.grey[50] : 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.grey[50],
        },
        borderRadius: '8px',
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: theme.palette.grey[900],
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      {sortOrder === value && (
        <CheckIcon
          sx={{
            color: theme.palette.success.main,
            fontSize: '1.25rem',
          }}
        />
      )}
    </Stack>
  );

  return (
    <Box>
      <IconButton
        onClick={handleClick}
        sx={{
          padding: '0.575rem 0.75rem',
          border: `1px solid ${theme.palette.grey[300]}`,
          borderRadius: '0.5rem',
          backgroundColor: 'white',
          '&:hover': {
            backgroundColor: theme.palette.grey[50],
          },
          display: 'flex',
          gap: '0.5rem',
          width: 'auto'
        }}
      >
        <PopulationIcon width="20px" height="20px" />
        {sortOrder && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: theme.palette.grey[900],
              fontWeight: 500,
            }}
          >
            {sortOrder === 'highest' ? 'Highest to lowest' : 'Lowest to highest'}
          </Typography>
        )}
      </IconButton>
      
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: '12px',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            minWidth: '240px',
          },
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            padding: '1rem',
          }}
        >
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: theme.palette.grey[900],
              marginBottom: '0.75rem',
            }}
          >
            {title}
          </Typography>
          <Stack spacing={0.5}>
            <Option value="highest" label="Highest to lowest" />
            <Option value="lowest" label="Lowest to highest" />
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
};

export default FilterComponent; 